import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { FeedbackEvent } from '@bonusly/common-feedback';
import { ArchiveOutline, Box, ChatAltOutline, GiftOutline, HeartOutline, PlusSquareSolid, rem, TrophyOutline } from '@bonusly/design-system';
import { useDispatch } from '@bonusly/events';
import { Permission, ProtectedContent, ProtectedContentWithPermissionOrFeatureFlag } from '@bonusly/who-am-i';

import { GiveBoxEvent } from '../../../components/recognition/give-box/events';
import { useNominationsCount } from '../../../queries/nominations';
import { useAwardData } from '../../bonus-feed/features/recognition/components/Awards/hooks/useAwardData';
import { useAwardModal } from '../../bonus-feed/features/recognition/components/Awards/hooks/useAwardModal';
import { useCheckInModal } from '../../check-ins/hooks/use-checkin-modal';
import { useNominationModal } from '../../nominations/hooks';
import { useCheckInsConfigured } from '../../team-management/hooks/use-check-ins-configured';
import { IconAndTextContent } from './icon-and-text-content';
import { UserNavAction } from './user-nav-action';
import { UserNavMenu } from './user-nav-menu';

type CreateNewMenuProps = {
	isExpanded: boolean;
};

export const CreateNewMenu: FC<CreateNewMenuProps> = ({ isExpanded }) => {
	const { t } = useTranslation('navigation');
	const dispatch = useDispatch();

	const { onOpen: handleOpenCheckInModal } = useCheckInModal();
	const { onOpen: handleAwardModal } = useAwardModal();
	const { onOpen: handleNominateModal } = useNominationModal();

	const checkInsEnabled = useCheckInsConfigured();
	const hasAwardsToGive = useAwardData().shouldDisplayBanner;
	const { open: nominationsOpen } = useNominationsCount();

	return (
		<Box mt={rem(8)}>
			<UserNavMenu buttonIcon={<PlusSquareSolid size="md" color="blue.500" />} buttonText={t('createNewButtonText')} isExpanded={isExpanded}>
				{/* GIVE RECOGNITION */}
				<ProtectedContent permissions={Permission.CanGiveRecognition}>
					<UserNavAction onClick={() => dispatch(GiveBoxEvent.Open)} isExpanded data-testid="nav-action-recognition">
						<IconAndTextContent icon={<GiftOutline size="md" />} label={t('createNew.recognition')} isExpanded />
					</UserNavAction>
				</ProtectedContent>

				{/* CHECK IN */}
				{checkInsEnabled && (
					<ProtectedContentWithPermissionOrFeatureFlag flag="manager-enablement-check-in" permissions={Permission.CanAccessCheckIn}>
						<UserNavAction onClick={() => handleOpenCheckInModal()} isExpanded data-testid="nav-action-check-in">
							<IconAndTextContent icon={<HeartOutline size="md" />} label={t('createNew.checkIn')} isExpanded />
						</UserNavAction>
					</ProtectedContentWithPermissionOrFeatureFlag>
				)}

				<ProtectedContentWithPermissionOrFeatureFlag flag={['one-on-ones', 'achieve-limited-release']} permissions={Permission.CanAccessOneOnOnes}>
					{/* ASK FOR ADVICE */}
					<UserNavAction onClick={() => dispatch(FeedbackEvent.OpenRequest)} isExpanded data-testid="nav-action-advice-request">
						<IconAndTextContent icon={<ChatAltOutline size="md" />} label={t('createNew.adviceRequest')} isExpanded />
					</UserNavAction>
				</ProtectedContentWithPermissionOrFeatureFlag>

				{/* TODO: Add goals - not global modal yet - https://bonusly1612893911.atlassian.net/browse/SHARK-3611 */}

				{/* AWARD */}
				{hasAwardsToGive && (
					<ProtectedContent permissions={Permission.CanAccessAwards}>
						<UserNavAction onClick={() => handleAwardModal()} isExpanded data-testid="nav-action-award">
							<IconAndTextContent icon={<TrophyOutline size="md" />} label={t('createNew.award')} isExpanded />
						</UserNavAction>
					</ProtectedContent>
				)}

				{/* NOMINATE TEAMMATE */}
				{nominationsOpen > 0 && (
					<ProtectedContent permissions={Permission.CanAccessNominations}>
						<UserNavAction onClick={() => handleNominateModal()} isExpanded data-testid="nav-action-nomination">
							<IconAndTextContent icon={<ArchiveOutline size="md" />} label={t('createNew.nomination')} isExpanded />
						</UserNavAction>
					</ProtectedContent>
				)}

				{/* TODO: Add make announcement - not global modal yet - https://bonusly1612893911.atlassian.net/browse/SHARK-3611 */}
			</UserNavMenu>
		</Box>
	);
};
